import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import UsersTree from './UsersTree';
import { useIsMobile } from '../../utils/useIsMobile';
import { useConfirm } from '../../components/shared/Confirm';

// Pestanas del modulo. "Recepcionistas" y "Clientes" del diseno no existen como
// rol en el sistema: los clientes viven en su propia tabla y se gestionan desde
// cada negocio, por eso no se listan aqui.
const TIPOS = [
  { key: 'estructura',      label: 'Estructura' },
  { key: 'propietarios',    label: 'Propietarios' },
  { key: 'administradores', label: 'Administradores' },
  { key: 'barberos',        label: 'Barberos' },
  { key: 'superadmins',     label: 'Super Admins' },
  { key: 'bloqueados',      label: 'Bloqueados' },
  { key: 'todos',           label: 'Todos' },
];

const fmtNum   = v => Number(v || 0).toLocaleString('es-CO');
const fmtFecha = f => {
  if (!f) return '—';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? String(f).slice(0, 10) : d.toLocaleDateString('es-CO');
};
const hace = f => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 60) return `Hace ${Math.max(1, min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h} h`;
  return `Hace ${Math.floor(h / 24)} d`;
};
const iniciales = n => String(n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const colorAvatar = n => {
  const c = ['#6366f1', '#0ea5e9', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  return c[String(n || '').length % c.length];
};

const toSlug = str => str.toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const roleLabel = r => ({ super_admin: 'Super Admin', admin_general: 'Admin General', admin: 'Administrador', barbero: 'Barbero' }[r] || r);
const roleColor = r => ({ super_admin: 'cancelled', admin_general: 'rescheduled', admin: 'confirmed', barbero: 'attended' }[r] || 'pending');

const EMPTY_FORM = { name: '', email: '', password: '', role: 'admin', business_id: '', plan_id: '', phone: '' };
const EMPTY_BIZ = { nombre: '', slug: '', ciudad: '', telefono: '', email: '' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = bizId => setCollapsed(prev => ({ ...prev, [bizId]: !prev[bizId] }));
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null); // null = crear, obj = editar
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const confirmar = useConfirm();

  // Modo de negocio: 'existing' | 'new'
  const [bizMode, setBizMode] = useState('existing');
  const [newBiz, setNewBiz] = useState(EMPTY_BIZ);

  // ── Listado del panel: tipo, indicadores, filtros y paginacion ────────────
  const esMovil = useIsMobile();
  const [tipo, setTipo]         = useState('estructura');
  // Se incrementa tras crear, editar o bloquear un usuario para que el arbol
  // se vuelva a pedir; no comparte el `load()` del listado plano.
  const [recargaArbol, setRecargaArbol] = useState(0);
  const refrescarArbol = () => setRecargaArbol(n => n + 1);
  const [resumen, setResumen]   = useState(null);
  const [ciudades, setCiudades] = useState([]);
  const [filtros, setFiltros]   = useState({ plan_id: '', estado: '', ciudad: '' });
  const [pagina, setPagina]     = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [totalPag, setTotalPag] = useState(1);
  const [totalUsu, setTotalUsu] = useState(0);

  const load = useCallback(async () => {
    // La pestaña de estructura tiene su propia consulta: pedir aqui el listado
    // plano seria trabajo tirado.
    if (tipo === 'estructura') { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { tipo, pagina, por_pagina: porPagina };
      if (search) params.search = search;
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });

      const [lista, res] = await Promise.all([
        api.get('/users/admin/list', { params }),
        api.get('/users/admin/summary', { params: { tipo } }),
      ]);
      setUsers(lista.data.usuarios || []);
      setTotalPag(lista.data.paginas || 1);
      setTotalUsu(lista.data.total || 0);
      setResumen(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cargando usuarios');
      setTimeout(() => setError(''), 5000);
    } finally { setLoading(false); }
  }, [tipo, search, filtros, pagina, porPagina]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/businesses').then(r => setBusinesses(r.data)).catch(() => {});
    api.get('/plans').then(r => setPlans(r.data)).catch(() => {});
    api.get('/users/admin/filters').then(r => setCiudades(r.data.ciudades || [])).catch(() => {});
  }, []);

  const cambiarFiltro = (campo, valor) => {
    setFiltros(f => ({ ...f, [campo]: valor }));
    setPagina(1);
  };

  const cambiarTipo = t => { setTipo(t); setPagina(1); };

  // Exporta a CSV lo que se ve en pantalla
  const exportar = () => {
    const cab = ['Usuario', 'Email', 'Rol', 'Negocio', 'Plan', 'Ciudad', 'Estado', 'Registro'];
    const filas = users.map(u => [
      u.nombre, u.email, roleLabel(u.rol), u.negocio || '', u.plan || '', u.ciudad || '',
      u.activo ? 'Activo' : 'Inactivo', fmtFecha(u.creado_en),
    ]);
    const csv = [cab, ...filas]
      .map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `usuarios-${tipo}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filteredBusinesses = form.plan_id
    ? businesses.filter(b => String(b.plan_id) === String(form.plan_id))
    : businesses;

  const handleBizNameChange = val => {
    setNewBiz(b => ({ ...b, nombre: val, slug: b.slug ? b.slug : toSlug(val) }));
  };

  const openModal = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setNewBiz(EMPTY_BIZ);
    setBizMode('existing');
    setError('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    // El listado del panel devuelve las columnas en español (nombre, rol);
    // se aceptan ambas formas para que el modal funcione en cualquier caso.
    setForm({
      phone: u.telefono || u.phone || '',
      name: u.nombre || u.name || '',
      email: u.email || '',
      password: '',
      role: u.rol || u.role || 'admin',
      business_id: u.negocio_id ? String(u.negocio_id) : '',
      plan_id: u.plan_id ? String(u.plan_id) : '',
    });
    setError('');
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editUser) {
        // Modo edición
        if (!form.name || !form.email) { setError('Nombre y correo son obligatorios'); setSaving(false); return; }
        // Si business_id está vacío en el form, preservar el original del usuario
        const businessId = form.business_id || editUser.negocio_id || null;
        await api.put(`/users/${editUser.id}`, {
          name: form.name,
          email: form.email,
          role: form.role,
          business_id: businessId,
          phone: form.phone,
        });
        // Actualizar plan solo si hay negocio válido y plan seleccionado
        const targetBiz = form.business_id || (editUser.negocio_id ? String(editUser.negocio_id) : null);
        if (targetBiz && targetBiz !== 'null' && form.plan_id) {
          await api.patch(`/businesses/${targetBiz}/plan`, { plan_id: form.plan_id });
        }
        setShowModal(false);
        load(); refrescarArbol();
        setSuccess('Usuario actualizado');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Modo creación
        if (!form.name || !form.email || !form.password) { setError('Nombre, correo y contraseña son obligatorios'); setSaving(false); return; }
        let businessId = form.business_id;

        if (form.role !== 'super_admin') {
          if (!form.plan_id) { setError('Selecciona un plan'); setSaving(false); return; }

          if (bizMode === 'new') {
            if (!newBiz.nombre || !newBiz.slug) { setError('Nombre y link del negocio son obligatorios'); setSaving(false); return; }
            const bizRes = await api.post('/businesses', {
              name: newBiz.nombre, slug: newBiz.slug, city: newBiz.ciudad,
              phone: newBiz.telefono, email: newBiz.email || form.email,
              plan_id: form.plan_id, address: '',
            });
            businessId = bizRes.data.id;
          } else {
            if (!businessId) { setError('Selecciona un negocio'); setSaving(false); return; }
          }
        }

        await api.post('/users', { ...form, business_id: businessId });
        setShowModal(false);
        setForm(EMPTY_FORM);
        setNewBiz(EMPTY_BIZ);
        load(); refrescarArbol();
        api.get('/businesses').then(r => setBusinesses(r.data));
        setSuccess('Usuario creado correctamente');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando usuario');
    } finally { setSaving(false); }
  };

  const toggleUser = async id => {
    try { await api.put(`/users/${id}/toggle`); load(); refrescarArbol(); }
    catch { setError('Error actualizando usuario'); }
  };

  const resetPassword = async (id, nombre) => {
    const newPass = await confirmar({
      titulo: 'Cambiar contraseña',
      mensaje: nombre ? `Nueva contraseña para ${nombre}.` : 'Escribe la nueva contraseña.',
      detalle: 'El usuario entrará con ella la próxima vez. Anótala antes de aceptar: no se vuelve a mostrar.',
      // De texto y no de tipo password: quien la fija tiene que poder leer lo
      // que escribio, porque es el quien se la pasa al usuario.
      campo: { etiqueta: 'Nueva contraseña', marcador: 'Mínimo 6 caracteres' },
      aceptar: 'Cambiar contraseña',
    });
    if (!newPass) return;
    if (String(newPass).length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setTimeout(() => setError(''), 4000);
      return;
    }
    try {
      await api.put(`/users/${id}/password`, { password: newPass });
      setSuccess('Contraseña actualizada');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Error actualizando contraseña'); }
  };

  const bookingOrigin = window.location.origin;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuarios — {TIPOS.find(t => t.key === tipo)?.label}</h1>
          <p className="page-subtitle">Gestiona todos los usuarios registrados en la plataforma</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={exportar}>⬇ Exportar</button>
          <button className="btn btn-primary" onClick={openModal}>+ Nuevo Usuario</button>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      {error && !showModal && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Pestañas por tipo de usuario */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1.25rem' }}>
        {TIPOS.map(t => {
          const activo = tipo === t.key;
          return (
            <button key={t.key} onClick={() => cambiarTipo(t.key)} style={{
              padding: '8px 15px', borderRadius: 9, cursor: 'pointer', fontSize: '0.84rem',
              fontWeight: activo ? 700 : 500, fontFamily: 'var(--font-body)',
              border: `1px solid ${activo ? 'var(--color-gold)' : 'var(--color-border)'}`,
              background: activo ? 'rgba(79,70,229,0.10)' : 'transparent',
              color: activo ? 'var(--color-gold)' : 'var(--color-text-muted)',
            }}>{t.label}</button>
          );
        })}
      </div>

      {/* La estructura es una vista aparte: arbol por negocio, sin los
          indicadores ni la paginacion del listado plano. */}
      {tipo === 'estructura' ? (
        <UsersTree
          negocios={businesses}
          recarga={recargaArbol}
          onEditar={openEdit}
          onAlternarEstado={u => toggleUser(u.id)}
          onReiniciarClave={u => resetPassword(u.id, u.nombre || u.name)}
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1fr 300px', gap: '1.25rem', alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          {/* Indicadores */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.9rem', marginBottom: '1.1rem',
          }}>
            {[
              { label: 'Total',      valor: resumen?.total,      color: '#6366f1' },
              { label: 'Activos',    valor: resumen?.activos,    color: '#16a34a',
                sub: resumen?.total ? `${Math.round((resumen.activos || 0) * 100 / resumen.total)}% del total` : null },
              { label: 'Inactivos',  valor: resumen?.inactivos,  color: '#ef4444',
                sub: resumen?.total ? `${Math.round((resumen.inactivos || 0) * 100 / resumen.total)}% del total` : null },
              { label: 'Nuevos este mes', valor: resumen?.nuevos_mes, color: '#2563eb',
                variacion: resumen?.variacion_nuevos },
            ].map(k => (
              <div key={k.label} style={{ background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '0.85rem 1rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: k.color }}>{fmtNum(k.valor)}</div>
                {k.sub && <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2 }}>{k.sub}</div>}
                {k.variacion !== undefined && k.variacion !== null && (
                  <div style={{ fontSize: '0.72rem', marginTop: 2, color: k.variacion >= 0 ? '#16a34a' : '#ef4444' }}>
                    {k.variacion >= 0 ? '↑' : '↓'} {Math.abs(k.variacion)}% vs mes anterior
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div style={{ background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '0.9rem 1rem', marginBottom: '1.1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
              <input className="form-input" placeholder="Buscar nombre, email o negocio..."
                value={search} onChange={e => { setSearch(e.target.value); setPagina(1); }} />
              <select className="form-select" value={filtros.plan_id} onChange={e => cambiarFiltro('plan_id', e.target.value)}>
                <option value="">Todos los planes</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
              </select>
              <select className="form-select" value={filtros.estado} onChange={e => cambiarFiltro('estado', e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
              <select className="form-select" value={filtros.ciudad} onChange={e => cambiarFiltro('ciudad', e.target.value)}>
                <option value="">Todas las ciudades</option>
                {ciudades.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Listado */}
          <div className="card">
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
            ) : users.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                No hay usuarios que coincidan
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      {!esMovil && <th>Negocio</th>}
                      {!esMovil && <th>Plan</th>}
                      <th>Estado</th>
                      {!esMovil && <th>Ciudad</th>}
                      {!esMovil && <th>Registro</th>}
                      <th className="col-acciones">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {u.url_foto
                              ? <img src={u.url_foto} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                              : <div style={{ width: 34, height: 34, borderRadius: '50%', background: colorAvatar(u.nombre), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{iniciales(u.nombre)}</div>}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>{u.nombre}</div>
                              <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>{u.email}</div>
                              <span className={`badge badge-${roleColor(u.rol)}`} style={{ fontSize: '0.62rem', marginTop: 3, display: 'inline-block' }}>{roleLabel(u.rol)}</span>
                            </div>
                          </div>
                        </td>
                        {!esMovil && <td style={{ fontSize: '0.84rem' }}>{u.negocio || '—'}</td>}
                        {!esMovil && <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{u.plan || '—'}</span></td>}
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: u.activo ? '#16a34a' : '#ef4444' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.activo ? '#16a34a' : '#ef4444' }} />
                            {u.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        {!esMovil && <td style={{ fontSize: '0.84rem' }}>{u.ciudad || '—'}</td>}
                        {!esMovil && <td style={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>{fmtFecha(u.creado_en)}</td>}
                        <td className="col-acciones">
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary btn-icon" title="Editar" onClick={() => openEdit(u)}>✏️</button>
                            <button className="btn btn-ghost btn-icon" title={u.activo ? 'Desactivar' : 'Activar'} onClick={() => toggleUser(u.id)}>
                              {u.activo ? '⛔' : '✅'}
                            </button>
                            <button className="btn btn-ghost btn-icon" title="Restablecer contraseña" onClick={() => resetPassword(u.id, u.nombre || u.name)}>🔑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Paginación */}
          {!loading && totalUsu > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
              <span>Mostrando {(pagina - 1) * porPagina + 1} a {Math.min(pagina * porPagina, totalUsu)} de {fmtNum(totalUsu)} usuarios</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost" style={{ padding: '4px 10px' }} disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>‹</button>
                <span style={{ padding: '0 6px' }}>Página <strong>{pagina}</strong> de {totalPag}</span>
                <button className="btn btn-ghost" style={{ padding: '4px 10px' }} disabled={pagina >= totalPag} onClick={() => setPagina(p => p + 1)}>›</button>
                <select className="form-select" style={{ padding: '4px 8px', fontSize: '0.8rem', width: 'auto' }}
                  value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / página</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* ── Panel lateral ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', minWidth: 0 }}>
          <div className="card" style={{ padding: '1rem 1.1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12 }}>Resumen de usuarios</div>
            {[
              { label: 'Activos',   valor: resumen?.activos,   color: '#16a34a' },
              { label: 'Inactivos', valor: resumen?.inactivos, color: '#ef4444' },
            ].map(x => {
              const pct = resumen?.total ? Math.round((x.valor || 0) * 100 / resumen.total) : 0;
              return (
                <div key={x.label} style={{ marginBottom: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 4 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: x.color }} />{x.label}
                    </span>
                    <strong>{fmtNum(x.valor)} <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>{pct}%</span></strong>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: 'var(--color-surface)' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: x.color }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card" style={{ padding: '1rem 1.1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12 }}>Usuarios por plan</div>
            {(resumen?.por_plan || []).length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Sin datos</div>
            ) : resumen.por_plan.map((p, i) => {
              const pct = resumen.total ? Math.round(Number(p.total) * 100 / resumen.total) : 0;
              const color = ['#8b5cf6', '#2563eb', '#f59e0b', '#16a34a'][i % 4];
              return (
                <div key={p.plan} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 3 }}>
                    <span>{p.plan}</span><strong>{fmtNum(p.total)} <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>{pct}%</span></strong>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: 'var(--color-surface)' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card" style={{ padding: '1rem 1.1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12 }}>Registros recientes</div>
            {(resumen?.recientes || []).length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Sin registros</div>
            ) : resumen.recientes.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 9, padding: '7px 0', borderBottom: i < resumen.recientes.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: colorAvatar(r.nombre), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                  {iniciales(r.nombre)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.nombre}</div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {roleLabel(r.rol)}{r.negocio ? ` · ${r.negocio}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{hace(r.creado_en)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

              {/* Datos del usuario */}
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              {!editUser && (
                <div className="form-group">
                  <label className="form-label">Contraseña *</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Celular con WhatsApp</label>
                <input className="form-input" inputMode="numeric" placeholder="573001234567"
                       value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                <p style={{ margin: '4px 2px 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  Con el 57 delante. Es donde llegan los avisos de la plataforma.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Rol</label>
                <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value, business_id: '', plan_id: '' })}>
                  <option value="super_admin">Super Admin</option>
                  <option value="admin_general">Admin General</option>
                  <option value="admin">Administrador</option>
                  <option value="barbero">Barbero</option>
                </select>
              </div>
              {/* En edición: negocio + plan */}
              {editUser && form.role !== 'super_admin' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Negocio</label>
                    <select className="form-select" value={form.business_id} onChange={e => {
                      const biz = businesses.find(b => String(b.id) === e.target.value);
                      setForm(f => ({ ...f, business_id: e.target.value, plan_id: String(biz?.plan_id || '') }));
                    }}>
                      <option value="">Sin negocio</option>
                      {businesses.map(b => <option key={b.id} value={String(b.id)}>{b.nombre}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <select className="form-select" value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
                      <option value="">Sin plan</option>
                      {plans.map(p => <option key={p.id} value={String(p.id)}>{p.nombre_visible}</option>)}
                    </select>
                  </div>
                </>
              )}

              {!editUser && form.role !== 'super_admin' && (
                <>
                  {/* Plan */}
                  <div className="form-group">
                    <label className="form-label">Plan *</label>
                    <select className="form-select" value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value, business_id: '' })}>
                      <option value="">Seleccionar plan...</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                    </select>
                  </div>

                  {form.plan_id && (
                    <>
                      {/* Selector modo negocio */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${bizMode === 'existing' ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ flex: 1 }}
                          onClick={() => setBizMode('existing')}
                        >
                          Negocio existente
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${bizMode === 'new' ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ flex: 1 }}
                          onClick={() => { setBizMode('new'); setForm(f => ({ ...f, business_id: '' })); }}
                        >
                          + Crear nuevo negocio
                        </button>
                      </div>

                      {bizMode === 'existing' ? (
                        <div className="form-group">
                          <label className="form-label">Negocio *</label>
                          <select className="form-select" value={form.business_id} onChange={e => setForm({ ...form, business_id: e.target.value })}>
                            <option value="">Seleccionar negocio...</option>
                            {filteredBusinesses.map(b => (
                              <option key={b.id} value={b.id}>{b.nombre}</option>
                            ))}
                          </select>
                          {filteredBusinesses.length === 0 && (
                            <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 6 }}>
                              No hay negocios con este plan. Usa "Crear nuevo negocio".
                            </p>
                          )}
                          {/* Mostrar link del negocio seleccionado */}
                          {form.business_id && (() => {
                            const biz = businesses.find(b => String(b.id) === String(form.business_id));
                            return biz?.slug ? (
                              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--color-surface-raised)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>Link de agendamiento de este negocio:</div>
                                <code style={{ fontSize: '0.82rem', color: 'var(--color-gold)', wordBreak: 'break-all' }}>
                                  {bookingOrigin}/book/{biz.slug}
                                </code>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        /* Formulario de nuevo negocio */
                        <div style={{ background: 'var(--color-surface-raised)', borderRadius: 10, padding: '16px', border: '1px solid var(--color-border)', marginBottom: 4 }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-gold)', fontWeight: 600, marginBottom: 14 }}>
                            Datos del nuevo negocio
                          </div>

                          <div className="form-group">
                            <label className="form-label">Nombre del negocio *</label>
                            <input
                              className="form-input"
                              placeholder="Ej: Barbería La 15"
                              value={newBiz.nombre}
                              onChange={e => handleBizNameChange(e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Link de agendamiento *
                              <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.75rem', marginLeft: 6 }}>
                                (unico por barberia)
                              </span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>/book/</span>
                              <input
                                className="form-input"
                                placeholder="mi-barberia"
                                value={newBiz.slug}
                                onChange={e => setNewBiz(b => ({ ...b, slug: toSlug(e.target.value) }))}
                                style={{ flex: 1 }}
                              />
                            </div>
                            {newBiz.slug && (
                              <div style={{ marginTop: 6, fontSize: '0.78rem', background: 'var(--color-surface)', padding: '6px 10px', borderRadius: 6, wordBreak: 'break-all' }}>
                                <span style={{ color: 'var(--color-text-muted)' }}>Link: </span>
                                <span style={{ color: 'var(--color-gold)', fontWeight: 600 }}>
                                  {bookingOrigin}/book/{newBiz.slug}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label className="form-label">Ciudad</label>
                            <input
                              className="form-input"
                              placeholder="Ej: Medellín"
                              value={newBiz.ciudad}
                              onChange={e => setNewBiz(b => ({ ...b, ciudad: e.target.value }))}
                            />
                          </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Teléfono del negocio</label>
                            <input
                              className="form-input"
                              placeholder="Ej: 3001234567"
                              value={newBiz.telefono}
                              onChange={e => setNewBiz(b => ({ ...b, telefono: e.target.value }))}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : editUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
